import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon,
  UserGroupIcon,
  CubeIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const BusinessAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [customerROI, setCustomerROI] = useState(null);
  const [seasonalData, setSeasonalData] = useState(null);
  const [productPerformance, setProductPerformance] = useState(null);
  const [period, setPeriod] = useState('quarter');

  useEffect(() => {
    loadAnalyticsData();
  }, [period]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [customerRes, seasonalRes, productRes] = await Promise.all([
        axios.get(`/api/business-analytics/customer-roi?period=${period}`, config),
        axios.get(`/api/business-analytics/seasonal-analysis?period=${period}&group_by=month`, config),
        axios.get(`/api/business-analytics/product-performance?period=${period}&limit=50`, config)
      ]);

      setCustomerROI(customerRes.data);
      setSeasonalData(seasonalRes.data);
      setProductPerformance(productRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      // Set empty data on error
      setCustomerROI({ customer_roi: [], overall_stats: [], insights: [], segments: {} });
      setSeasonalData({ seasonal_data: [], insights: [], seasonal_products: {} });
      setProductPerformance({ product_performance: [], insights: [], growth_analysis: {} });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₪${parseFloat(amount || 0).toLocaleString()}`;
  };

  const formatPercentage = (value) => {
    return `${parseFloat(value || 0).toFixed(1)}%`;
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* סטטיסטיקות מהירות */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <UserGroupIcon className="w-8 h-8 text-blue-500" />
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-600">לקוחות פעילים</p>
              <p className="text-2xl font-bold text-gray-900">
                {customerROI?.customer_roi?.length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <ChartBarIcon className="w-8 h-8 text-green-500" />
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-600">הכנסות כוללות</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(customerROI?.overall_stats?.[0]?.total_revenue)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <CubeIcon className="w-8 h-8 text-purple-500" />
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-600">מוצרים פעילים</p>
              <p className="text-2xl font-bold text-gray-900">
                {productPerformance?.product_performance?.length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <ArrowTrendingUpIcon className="w-8 h-8 text-indigo-500" />
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-600">מרווח רווח ממוצע</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatPercentage(customerROI?.overall_stats?.[0]?.overall_profit_margin)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* תובנות */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">תובנות עסקיות</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {customerROI?.insights?.map((insight, index) => (
              <div key={index} className="flex items-start space-x-3 space-x-reverse">
                {getInsightIcon(insight.type)}
                <div>
                  <p className="font-medium text-gray-900">{insight.title}</p>
                  <p className="text-sm text-gray-600">{insight.message}</p>
                </div>
              </div>
            ))}
            {seasonalData?.insights?.map((insight, index) => (
              <div key={`seasonal-${index}`} className="flex items-start space-x-3 space-x-reverse">
                {getInsightIcon(insight.type)}
                <div>
                  <p className="font-medium text-gray-900">{insight.title}</p>
                  <p className="text-sm text-gray-600">{insight.message}</p>
                </div>
              </div>
            ))}
            {productPerformance?.insights?.map((insight, index) => (
              <div key={`product-${index}`} className="flex items-start space-x-3 space-x-reverse">
                {getInsightIcon(insight.type)}
                <div>
                  <p className="font-medium text-gray-900">{insight.title}</p>
                  <p className="text-sm text-gray-600">{insight.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCustomerROI = () => (
    <div className="space-y-6">
      {/* סגמנטים של לקוחות */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {customerROI?.segments && Object.entries(customerROI.segments).map(([segment, data]) => (
          <div key={segment} className="bg-white rounded-lg shadow p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{data.count}</p>
              <p className="text-sm text-gray-600">
                {segment === 'vip' && 'לקוחות VIP'}
                {segment === 'high_value' && 'ערך גבוה'}
                {segment === 'regular' && 'רגילים'}
                {segment === 'low_value' && 'ערך נמוך'}
                {segment === 'at_risk' && 'בסיכון'}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* טבלת לקוחות מובילים */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">לקוחות מובילים לפי ROI</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  חברה
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  הזמנות
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  הכנסות
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  רווח גולמי
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ROI
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ממוצע הזמנה
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customerROI?.customer_roi?.slice(0, 10).map((customer, index) => (
                <tr key={customer.company_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{customer.company_name}</div>
                      <div className="text-sm text-gray-500">{customer.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {customer.total_orders}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(customer.total_revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(customer.gross_profit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      customer.roi_percentage > 100 
                        ? 'bg-green-100 text-green-800'
                        : customer.roi_percentage > 50
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {formatPercentage(customer.roi_percentage)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(customer.avg_order_value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSeasonalAnalysis = () => (
    <div className="space-y-6">
      {/* גרף עונתיות פשוט */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">מגמות מכירות לפי חודש</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {seasonalData?.seasonal_data?.map((period, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <CalendarIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{period.period}</p>
                    <p className="text-sm text-gray-500">{period.total_orders} הזמנות</p>
                  </div>
                </div>
                <div className="text-left">
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(period.total_revenue)}
                  </p>
                  <p className="text-sm text-gray-500">
                    ממוצע: {formatCurrency(period.avg_order_value)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* מוצרים פופולריים לפי עונה */}
      {seasonalData?.seasonal_products && Object.keys(seasonalData.seasonal_products).length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">מוצרים פופולריים לפי תקופה</h3>
          </div>
          <div className="p-6">
            {Object.entries(seasonalData.seasonal_products).map(([period, products]) => (
              <div key={period} className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{period}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.slice(0, 6).map((product, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-sm text-gray-900">{product.product_name}</p>
                      <p className="text-xs text-gray-500">
                        {product.total_quantity} יחידות • {formatCurrency(product.total_revenue)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderProductPerformance = () => (
    <div className="space-y-6">
      {/* מוצרים מובילים */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">מוצרים מובילים</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  מוצר
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  כמות נמכרה
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  הכנסות
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  רווח
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  מרווח רווח
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  מלאי
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {productPerformance?.product_performance?.slice(0, 15).map((product, index) => (
                <tr key={product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{product.product_name}</div>
                      <div className="text-sm text-gray-500">{product.sku}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {product.total_quantity_sold}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(product.total_revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(product.total_profit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      product.profit_margin_percentage > 30 
                        ? 'bg-green-100 text-green-800'
                        : product.profit_margin_percentage > 15
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {formatPercentage(product.profit_margin_percentage)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm ${
                      product.stock_quantity < 10 
                        ? 'text-red-600 font-medium'
                        : product.stock_quantity < 50
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    }`}>
                      {product.stock_quantity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* מוצרים בעלייה/ירידה */}
      {productPerformance?.growth_analysis && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <ArrowTrendingUpIcon className="w-5 h-5 text-green-500 ml-2" />
                  מוצרים בעלייה
                </h3>
              </div>
            <div className="p-6">
              <div className="space-y-3">
                {productPerformance.growth_analysis.top_growing?.slice(0, 5).map((product, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm text-gray-900">{product.product_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatCurrency(product.current_revenue)}
                      </p>
                    </div>
                    <span className="text-green-600 font-semibold">
                      +{formatPercentage(product.revenue_growth_percentage)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <ArrowTrendingDownIcon className="w-5 h-5 text-red-500 ml-2" />
                מוצרים בירידה
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {productPerformance.growth_analysis.declining?.slice(0, 5).map((product, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm text-gray-900">{product.product_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatCurrency(product.current_revenue)}
                      </p>
                    </div>
                    <span className="text-red-600 font-semibold">
                      {formatPercentage(product.revenue_growth_percentage)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען נתוני ניתוח...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* כותרת */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">ניתוח עסקי מתקדם</h1>
          <p className="mt-2 text-gray-600">תובנות ונתונים לקבלת החלטות עסקיות חכמות</p>
        </div>

        {/* בחירת תקופה */}
        <div className="mb-6 flex flex-wrap gap-4 items-center">
          <div className="flex items-center space-x-2 space-x-reverse">
            <label className="text-sm font-medium text-gray-700">תקופה:</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="week">שבוע אחרון</option>
              <option value="month">חודש אחרון</option>
              <option value="quarter">רבעון אחרון</option>
              <option value="year">שנה אחרונה</option>
            </select>
          </div>
          <button
            onClick={loadAnalyticsData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            רענן נתונים
          </button>
        </div>

        {/* טאבים */}
        <div className="mb-6">
          <nav className="flex space-x-8 space-x-reverse">
            {[
              { id: 'overview', name: 'סקירה כללית', icon: ChartBarIcon },
              { id: 'customers', name: 'ניתוח לקוחות', icon: UserGroupIcon },
              { id: 'seasonal', name: 'ניתוח עונתי', icon: CalendarIcon },
              { id: 'products', name: 'ביצועי מוצרים', icon: CubeIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-5 h-5 ml-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* תוכן */}
        <div>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'customers' && renderCustomerROI()}
          {activeTab === 'seasonal' && renderSeasonalAnalysis()}
          {activeTab === 'products' && renderProductPerformance()}
        </div>
      </div>
    </div>
  );
};

export default BusinessAnalytics;
